import { useState, useMemo } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { t } from '../../lib/translations';
import { ADDITIONAL_SERVICES } from '../../lib/pricingLogic';
import { Input } from '../ui/input';
import { Separator } from '../ui/separator';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '../ui/command';
import {
  Globe, Scale, Calendar, FolderOpen, Package, Languages, Rocket, Building2,
  BookOpen, Heart, FileInput, RefreshCw, Check, X, Plus, ChevronsUpDown, Search,
} from 'lucide-react';

const SPEC_ICONS = {
  foreign_transactions: Globe,
  mixed_vat: Scale,
  cash_vat: Calendar,
  eu_projects: FolderOpen,
  warehouse: Package,
  foreign_language: Languages,
  startup: Rocket,
  estonian_cit: Building2,
  team5_accounts: BookOpen,
  foundation: Heart,
  file_import: FileInput,
  vat_refunds: RefreshCw,
};

const ALL_SPECIFICS = [
  'foreign_transactions', 'mixed_vat', 'cash_vat', 'eu_projects',
  'warehouse', 'foreign_language', 'startup', 'estonian_cit',
  'team5_accounts', 'foundation', 'file_import', 'vat_refunds',
];

const SERVICE_CATEGORIES = [
  {
    id: 'admin',
    pl: 'Opłaty i administracja',
    en: 'Fees & Administration',
    services: ['admin_fee', 'late_docs', 'archive', 'backlog', 'suspension'],
  },
  {
    id: 'pit',
    pl: 'PIT i rozliczenia roczne',
    en: 'PIT & Annual Returns',
    services: ['pit_annual', 'pit_self', 'pit_spouse_2', 'pit_spouse_5', 'pit_advance', 'pit_correction', 'pit_4'],
  },
  {
    id: 'vat',
    pl: 'VAT',
    en: 'VAT',
    services: ['vat_r_update', 'vat_r_reg', 'vat_other', 'vat_26', 'vat_zd', 'vat_proportion'],
  },
  {
    id: 'ipbox',
    pl: 'IP BOX',
    en: 'IP BOX',
    services: ['ipbox_monthly', 'ipbox_annual'],
  },
  {
    id: 'corrections',
    pl: 'JPK i korekty',
    en: 'JPK & Corrections',
    services: ['jpk_correction', 'recalc_month'],
  },
  {
    id: 'assets',
    pl: 'Środki trwałe i amortyzacja',
    en: 'Fixed Assets & Depreciation',
    services: ['fixed_asset_record', 'fixed_asset_new', 'depreciation', 'remanent'],
  },
  {
    id: 'trips',
    pl: 'Delegacje',
    en: 'Business Trips',
    services: ['delegation_pl', 'delegation_foreign'],
  },
  {
    id: 'reports',
    pl: 'Deklaracje i sprawozdania',
    en: 'Declarations & Reports',
    services: ['analysis', 'gus', 'donation', 'intrastat', 'ift_cit', 'pcc'],
  },
  {
    id: 'legal',
    pl: 'Pisma i reprezentacja',
    en: 'Letters & Representation',
    services: ['letters', 'representation', 'credit_app', 'installments', 'certificate', 'czynny_zal'],
  },
  {
    id: 'other',
    pl: 'Pozostałe',
    en: 'Other',
    services: ['nota', 'mail', 'scans'],
  },
];

export default function Step4Specifics({ formData, updateField }) {
  const { language } = useLanguage();
  const [selectedService, setSelectedService] = useState('');
  const [serviceQty, setServiceQty] = useState(1);
  const [comboOpen, setComboOpen] = useState(false);

  const ccy = t('common.currency', language);

  const serviceMap = useMemo(() => {
    const map = {};
    ADDITIONAL_SERVICES.forEach(s => { map[s.id] = s; });
    return map;
  }, []);

  const selectedServiceLabel = useMemo(() => {
    if (!selectedService) return '';
    const s = serviceMap[selectedService];
    return s ? (language === 'pl' ? s.pl : s.en) : '';
  }, [selectedService, serviceMap, language]);

  const toggleSpecific = (id) => {
    const current = formData.businessSpecifics;
    const updated = current.includes(id)
      ? current.filter(s => s !== id)
      : [...current, id];
    updateField('businessSpecifics', updated);
  };

  const addService = () => {
    if (!selectedService) return;
    const service = serviceMap[selectedService];
    if (!service) return;

    const existing = formData.additionalServices.find(s => s.service_id === selectedService);
    let updated;
    if (existing) {
      updated = formData.additionalServices.map(s =>
        s.service_id === selectedService
          ? { ...s, quantity: s.quantity + serviceQty, total: (s.quantity + serviceQty) * s.unit_price }
          : s
      );
    } else {
      updated = [
        ...formData.additionalServices,
        {
          service_id: service.id,
          quantity: serviceQty,
          name: language === 'pl' ? service.pl : service.en,
          unit_price: service.price,
          total: serviceQty * service.price,
        },
      ];
    }
    updateField('additionalServices', updated);
    setSelectedService('');
    setServiceQty(1);
  };

  const removeService = (serviceId) => {
    updateField('additionalServices', formData.additionalServices.filter(s => s.service_id !== serviceId));
  };

  const additionalTotal = formData.additionalServices.reduce((sum, s) => sum + s.total, 0);

  return (
    <div data-testid="step-4-specifics">
      <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1B3A7B] mb-2">
        {t('step4.title', language)}
      </h2>
      <p className="text-slate-500 mb-8 text-base">
        {t('step4.subtitle', language)}
      </p>

      {/* Business Specifics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-10">
        {ALL_SPECIFICS.map((specId) => {
          const Icon = SPEC_ICONS[specId];
          const active = formData.businessSpecifics.includes(specId);
          const specData = t(`step4.specifics.${specId}`, language);

          return (
            <div
              key={specId}
              onClick={() => toggleSpecific(specId)}
              data-testid={`specific-${specId}`}
              className={`specifics-card cursor-pointer rounded-xl border p-4 transition-all ${
                active ? 'active' : 'border-slate-200 bg-white'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  active ? 'bg-[#1B3A7B] text-white' : 'bg-slate-100 text-slate-500'
                }`}>
                  <Icon className="w-4 h-4" />
                </div>
                {active && (
                  <div className="w-5 h-5 rounded-full bg-[#1B3A7B] flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
              </div>
              <h4 className={`font-medium text-sm mb-1 ${active ? 'text-[#1B3A7B]' : 'text-slate-800'}`}>
                {specData.name}
              </h4>
              <p className="text-xs text-slate-500 mb-2">{specData.desc}</p>
              <span className={`inline-block text-xs font-mono-price font-semibold px-2 py-0.5 rounded ${
                specData.mod.startsWith('-')
                  ? 'bg-green-50 text-green-700'
                  : 'bg-orange-50 text-orange-700'
              }`}>
                {specData.mod}
              </span>
            </div>
          );
        })}
      </div>

      <Separator className="my-8" />

      {/* Additional Services */}
      <h3 className="font-heading text-2xl font-semibold text-[#1B3A7B] mb-2">
        {t('step4.additionalTitle', language)}
      </h3>
      <p className="text-slate-500 mb-6 text-sm">
        {t('step4.additionalSubtitle', language)}
      </p>

      {/* Searchable Service Selector */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex-1">
          <Popover open={comboOpen} onOpenChange={setComboOpen}>
            <PopoverTrigger asChild>
              <button
                role="combobox"
                aria-expanded={comboOpen}
                data-testid="additional-service-select"
                className="w-full h-11 px-3 rounded-md border border-slate-300 bg-white text-left text-sm flex items-center justify-between hover:border-slate-400 transition-colors"
              >
                <span className={selectedService ? 'text-slate-800' : 'text-slate-500'}>
                  {selectedService ? selectedServiceLabel : (language === 'pl' ? 'Wyszukaj usługę...' : 'Search service...')}
                </span>
                <ChevronsUpDown className="w-4 h-4 text-slate-400 shrink-0" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
              <Command>
                <CommandInput
                  placeholder={language === 'pl' ? 'Szukaj usługi...' : 'Search services...'}
                  data-testid="service-search-input"
                />
                <CommandList className="max-h-64">
                  <CommandEmpty>{language === 'pl' ? 'Nie znaleziono usługi' : 'No services found'}</CommandEmpty>
                  {SERVICE_CATEGORIES.map((cat) => {
                    const catServices = cat.services
                      .map(id => serviceMap[id])
                      .filter(Boolean);
                    if (catServices.length === 0) return null;
                    return (
                      <CommandGroup key={cat.id} heading={language === 'pl' ? cat.pl : cat.en}>
                        {catServices.map(service => (
                          <CommandItem
                            key={service.id}
                            value={`${language === 'pl' ? service.pl : service.en} ${service.id}`}
                            onSelect={() => {
                              setSelectedService(service.id);
                              setComboOpen(false);
                            }}
                            data-testid={`service-option-${service.id}`}
                            className="flex items-center justify-between"
                          >
                            <span className="text-sm truncate mr-2">{language === 'pl' ? service.pl : service.en}</span>
                            <span className="font-mono-price text-xs text-slate-500 shrink-0">
                              {service.price} {ccy}/{service.unit}
                            </span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    );
                  })}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        <div className="flex gap-2">
          <Input
            type="number"
            value={serviceQty}
            onChange={(e) => setServiceQty(Math.max(1, parseInt(e.target.value) || 1))}
            min={1}
            max={99}
            className="w-20 h-11 text-center font-mono-price border-slate-300"
            data-testid="service-quantity-input"
          />
          <button
            onClick={addService}
            disabled={!selectedService}
            className={`h-11 px-4 rounded-md font-medium text-sm flex items-center gap-1.5 transition-all ${
              selectedService
                ? 'bg-[#1B3A7B] text-white hover:bg-[#142d63]'
                : 'bg-slate-100 text-slate-400 cursor-not-allowed'
            }`}
            data-testid="add-service-btn"
          >
            <Plus className="w-4 h-4" />
            {t('common.add', language)}
          </button>
        </div>
      </div>

      {/* Added Services List */}
      {formData.additionalServices.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-4">
          <div className="p-3 bg-slate-50 border-b border-slate-200">
            <h4 className="text-sm font-medium text-slate-700">{t('step4.addedServices', language)}</h4>
          </div>
          <div className="divide-y divide-slate-100">
            {formData.additionalServices.map((svc) => (
              <div key={svc.service_id} className="p-3 flex items-center justify-between" data-testid={`added-service-${svc.service_id}`}>
                <div className="flex-1">
                  <span className="text-sm text-slate-800">{svc.name}</span>
                  <span className="text-xs text-slate-500 ml-2">
                    x {svc.quantity} = <span className="font-mono-price font-semibold">{svc.total} {ccy}</span>
                  </span>
                </div>
                <button
                  onClick={() => removeService(svc.service_id)}
                  className="w-7 h-7 rounded-md flex items-center justify-center text-slate-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                  data-testid={`remove-service-${svc.service_id}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <div className="p-3 bg-slate-50 border-t border-slate-200 flex justify-between items-center">
            <span className="text-sm font-medium text-slate-600">{t('step4.sumAdditional', language)}</span>
            <span className="font-mono-price font-bold text-[#E8941A]">{additionalTotal} {ccy}</span>
          </div>
        </div>
      )}
    </div>
  );
}
